import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, BookOpen, FileText, Library, ExternalLink } from "lucide-react";
import { EASE } from "./common";
import { TChapter, useLang, t } from "../i18n";
import { PUBLICATIONS } from "../data/cv";

const FILTERS = [
  { key: "all", labelKey: "filter_all", icon: Library },
  { key: "article", labelKey: "filter_journals", icon: FileText },
  { key: "chapter", labelKey: "filter_chapters", icon: BookOpen },
  { key: "book", labelKey: "filter_books", icon: Library },
];

const TAG_COLORS = {
  Scopus: "bg-crimson/10 text-crimson",
  WoS: "bg-saffron/10 text-saffron",
  ABDC: "bg-emerald/10 text-emerald",
  Q1: "bg-crimson/10 text-crimson",
  International: "bg-cobalt/10 text-cobalt",
};

const Publications = () => {
  const { lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    return PUBLICATIONS.filter((p) => (filter === "all" || p.type === filter))
      .filter((p) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return p.title.toLowerCase().includes(q) || p.venue.toLowerCase().includes(q);
      })
      .sort((a, b) => b.year - a.year);
  }, [filter, query]);

  return (
    <section id="publications" className="relative bg-sand py-24 sm:py-36" data-testid="section-publications">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <TChapter num="02" k="c2" />

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
          <div className="flex flex-wrap gap-2" data-testid="publication-filters">
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`publication-filter-${f.key === "article" ? "journals" : f.key === "chapter" ? "chapters" : f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                    active ? "bg-ink text-bone border-ink" : "bg-panel/60 text-ink/70 border-ink/15 hover:border-ink/40"
                  }`}
                >
                  <Icon size={13} />
                  {t(lang, f.labelKey)}
                  <span className={active ? "text-saffron" : "text-mutedink"}>
                    {f.key === "all" ? PUBLICATIONS.length : PUBLICATIONS.filter((p) => p.type === f.key).length}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative md:ml-auto">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-mutedink" />
            <input
              data-testid="publication-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(lang, "search_placeholder")}
              className="w-full md:w-72 bg-panel border border-ink/15 rounded-full pl-11 pr-4 py-2.5 text-sm text-ink placeholder:text-mutedink/70 focus:outline-none focus:border-crimson/50 transition-colors"
            />
          </div>
        </div>

        <div className="border-t border-ink/10" data-testid="publication-list">
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => (
              <motion.article
                key={`${p.type}-${p.year}-${p.title.slice(0, 40)}`}
                layout
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.03, 0.4), ease: EASE }}
                className="group grid grid-cols-[auto_1fr_auto] items-start gap-4 sm:gap-8 py-6 border-b border-ink/10 hover:bg-panel/50 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-lg transition-colors"
                data-testid={`publication-item-${i}`}
              >
                <span className="font-mono text-xs text-mutedink pt-1.5 w-8">{String(i + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-ink leading-snug group-hover:text-crimson transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-mutedink leading-relaxed">{p.venue}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {p.tags.map((t) => (
                      <span key={t} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${TAG_COLORS[t] || "bg-ink/5 text-mutedink"}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 pt-1">
                  <span className="font-display font-bold text-2xl text-ink/25 group-hover:text-crimson/60 transition-colors">{p.year}</span>
                  {p.link && (
                    <a
                      data-testid={`publication-link-${i}`}
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-cobalt hover:text-crimson transition-colors"
                    >
                      DOI <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
          {list.length === 0 && (
            <p className="py-16 text-center font-mono text-sm text-mutedink" data-testid="publication-empty">{t(lang, "pubs_empty")}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Publications;
